import { useCallback, useMemo, useRef, useState } from 'react';
import {
    ApolloError,
    DocumentNode,
    OperationVariables,
    QueryResult,
    useQuery,
} from '@apollo/client';
import isEqual from 'lodash.isequal';
import { useDebounce } from './useDebounce';

type PaginationMode = 'page' | 'offset';

interface UsePaginatedQueryOptions<TData, TVariables extends OperationVariables = OperationVariables> {
    /** GraphQL query document */
    query: DocumentNode;
    /** Query variables (excluding pagination) */
    variables?: Omit<TVariables, 'pagination'>;
    /** User-selected page size: 10, 20, 50, 100 */
    pageSize: number;
    /** Current page (0-indexed) */
    page: number;
    /** Initial number of rows fetched on first load. Subsequent `loadMore` calls expand
     *  the limit by this same value until `pageSize` is reached. Default: 10 */
    baseLimit?: number;
    /** Pagination mode: 'page' sends { page, limit }, 'offset' sends { offset, limit }. Default: 'page' */
    paginationMode?: PaginationMode;
    /** Skip query execution */
    skip?: boolean;
    /** Extract docs and totalDocs from query result */
    extractResult: (data: TData) => {
        docs: any[];
        totalDocs: number;
        meta: {
            page: number;
            totalPages: number;
            totalDocs: number;
            nextPage: number | null;
            prevPage: number | null;
        };
    };
}

interface UsePaginatedQueryResult<T> {
    /** Accumulated rows for current page */
    rows: T[];
    /** Total document count from backend */
    totalDocs: number;
    /** True only on initial page load (first chunk) */
    loading: boolean;
    /** True when fetching subsequent chunks via scroll */
    isLoadingMore: boolean;
    /** Whether more chunks remain for current page */
    hasMore: boolean;
    /** Trigger next chunk fetch */
    loadMore: () => void;
    /** Apollo error */
    error: ApolloError | undefined;
    /** Refetch from scratch (resets chunks) */
    refetch: () => void;
    /** Meta pagionation data */
    meta: {
        page: number;
        totalPages: number;
        totalDocs: number;
        nextPage: number | null;
        prevPage: number | null;
    }
}

/**
 * Universal paginated GraphQL query hook.
 *
 * Pagination strategy: **growing limit**, not chunk-merge.
 *
 * Instead of issuing one request per chunk and merging the results in cache (which is
 * fragile because `fetchMore` + `updateQuery` races with Apollo's watchQuery refetch
 * behavior — and could cause the visible list to flash back to the first chunk), this
 * hook issues a SINGLE useQuery whose `limit` grows by `baseLimit` on each `loadMore`.
 *
 *   - Initial fetch: `{ offset: page*pageSize, limit: baseLimit }`        → 10 rows
 *   - loadMore:      `{ offset: page*pageSize, limit: baseLimit*2 }`      → 20 rows
 *   - loadMore:      `{ offset: page*pageSize, limit: baseLimit*3 }`      → 30 rows
 *
 * Each variant is a distinct cache entry. Apollo's normalized cache deduplicates the
 * underlying entities, so the marginal payload is just the new rows. Per-row mutation
 * reactivity is preserved: writing to a normalized entity by id reactively updates
 * whichever paginated entry is currently rendered.
 *
 * Page/pageSize/variables changes reset `currentLimit` back to `baseLimit`.
 */
export const usePaginatedQuery = <
    T = any,
    TData = any,
    TVariables extends OperationVariables = OperationVariables,
>({
    query,
    variables,
    pageSize,
    page,
    baseLimit = 10,
    paginationMode = 'page',
    skip = false,
    extractResult,
}: UsePaginatedQueryOptions<TData, TVariables>): UsePaginatedQueryResult<T> => {

    const emptyMeta = useMemo(() => ({
        page: 0,
        totalPages: 0,
        totalDocs: 0,
        nextPage: null,
        prevPage: null,
    }), []);

    // Stabilize variables: consumers commonly pass a fresh object literal each render.
    // Holding onto the previous reference when contents are deep-equal prevents Apollo
    // from re-issuing the network request on identity churn alone.
    const stableVariablesRef = useRef(variables);
    if (!isEqual(stableVariablesRef.current, variables)) {
        stableVariablesRef.current = variables;
    }
    const stableVariables = stableVariablesRef.current;

    // Effective baseLimit cannot exceed pageSize (otherwise loadMore makes no sense).
    const effectiveBaseLimit = Math.min(baseLimit, pageSize);

    // currentLimit is the limit currently sent to the backend for this logical page.
    // It starts at effectiveBaseLimit and grows by effectiveBaseLimit on each loadMore.
    const [currentLimit, setCurrentLimit] = useState(effectiveBaseLimit);

    // Reset currentLimit synchronously during render whenever the logical page identity
    // (page / pageSize / variables) changes, so the next useQuery call uses the reset
    // value rather than the carried-over one.
    const variablesKey = useMemo(() => JSON.stringify(stableVariables), [stableVariables]);
    const resetKey = `${page}-${pageSize}-${variablesKey}`;
    const lastResetKeyRef = useRef(resetKey);
    if (lastResetKeyRef.current !== resetKey) {
        lastResetKeyRef.current = resetKey;
        if (currentLimit !== effectiveBaseLimit) {
            setCurrentLimit(effectiveBaseLimit);
        }
    }

    // Build pagination variable for the current limit. 
    const paginationVariable = useMemo(() => {
        if (paginationMode === 'offset') {
            return { offset: page * pageSize, limit: currentLimit };
        }
        // 'page' mode: anchor at the start of the logical page and ask for currentLimit
        // rows. This works correctly when the consumer's pageSize equals baseLimit
        // (the common case for 'page' mode); growing-limit chunking is intended for
        // 'offset' mode.
        return { page: page + 1, limit: currentLimit };
    }, [page, pageSize, currentLimit, paginationMode]);

    const queryVariables = useMemo(
        () => ({
            ...stableVariables,
            pagination: paginationVariable,
        } as unknown as TVariables),
        [stableVariables, paginationVariable]
    );

    const debouncedQueryVariables = useDebounce(queryVariables, 300);

    const {
        data,
        previousData,
        loading: queryLoading,
        error,
        refetch: apolloRefetch,
    }: QueryResult<TData, TVariables> = useQuery<TData, TVariables>(query, {
        variables: debouncedQueryVariables,
        skip,
        // cache-first: if we've already fetched these exact variables, serve from cache
        // instantly. When loadMore expands `currentLimit`, variables change → cache
        // miss → network fetch. When a mutation updates a normalized entity in the
        // list, Apollo broadcasts the change to this watchQuery automatically.
        fetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
    });

    // While a larger-limit fetch is in flight, keep showing the previously-rendered
    // (smaller) result instead of an empty list. Prevents a "flash to empty" on
    // loadMore.
    const displayData = data ?? previousData;

    const extractedResult = useMemo(() => {
        if (!displayData) {
            return {
                docs: [] as T[],
                totalDocs: 0,
                meta: emptyMeta,
            };
        }

        return extractResult(displayData) as {
            docs: T[];
            totalDocs: number;
            meta: typeof emptyMeta;
        };
    }, [displayData, extractResult, emptyMeta]);

    const rows = extractedResult.docs;
    const totalDocs = extractedResult.totalDocs;
    const meta = extractedResult.meta;

    const expectedRowsForPage = useMemo(() => {
        const remainingRows = Math.max(totalDocs - page * pageSize, 0);
        return Math.min(pageSize, remainingRows);
    }, [totalDocs, page, pageSize]);

    // We can ask for more if (a) the current limit hasn't reached the user's pageSize
    // and (b) the backend still has rows beyond what we've loaded.
    const limitCanGrow = currentLimit < pageSize;
    const backendHasMore = rows.length < expectedRowsForPage;
    const hasMore = limitCanGrow && backendHasMore;

    const isInitialLoading = queryLoading && rows.length === 0;
    const isLoadingMore = queryLoading && rows.length > 0;

    const loadMore = useCallback(() => {
        if (skip) return;
        // Functional update so rapid concurrent calls coalesce on the latest value.
        setCurrentLimit((prev) => {
            if (prev >= pageSize) return prev;
            return Math.min(prev + effectiveBaseLimit, pageSize);
        });
    }, [skip, pageSize, effectiveBaseLimit]);

    // refetch: reset growing-limit and re-issue from scratch.
    const refetch = useCallback(() => {
        setCurrentLimit(effectiveBaseLimit);
        return apolloRefetch({
            ...stableVariables,
            pagination:
                paginationMode === 'offset'
                    ? { offset: page * pageSize, limit: effectiveBaseLimit }
                    : { page: page + 1, limit: effectiveBaseLimit },
        } as unknown as TVariables);
    }, [apolloRefetch, stableVariables, paginationMode, page, pageSize, effectiveBaseLimit]);

    return {
        rows,
        totalDocs,
        loading: isInitialLoading,
        isLoadingMore,
        hasMore,
        loadMore,
        error,
        refetch,
        meta,
    };
};

export default usePaginatedQuery;
